import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { saveProtocol, approveProtocol, addProtocolApprover } from '../services/ApiService';
import type { Protocol } from '../types/models';
import {
  FileText, X, UserCheck, CheckCircle2,
  AlertCircle, Clock, Loader2, Plus, MessageSquare,
} from 'lucide-react';

interface Props {
  orgId: string;
  meetingId: string;
  protocol: Protocol | null;
  onProtocolChange: (p: Protocol | null) => void;
}

export default function MeetingProtocol({ orgId, meetingId, protocol, onProtocolChange }: Props) {
  const { user } = useAuth();
  const { isBoardMember, members } = useOrganization();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [showApproverPicker, setShowApproverPicker] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [showCommentFor, setShowCommentFor] = useState<string | null>(null);
  const [submittingApproval, setSubmittingApproval] = useState(false);

  useEffect(() => {
    if (protocol) {
      setTitle(protocol.title);
      setContent(protocol.content);
    }
  }, [protocol]);

  const handleSave = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;
    setSaving(true);
    try {
      const saved = await saveProtocol(orgId, meetingId, { title, content });
      // The backend resets every approval when the text changes, so the protocol has to be justerat again.
      const changed = protocol !== null && (protocol.title !== title || protocol.content !== content);
      const approvals = (protocol?.approvals ?? []).map(a => (changed ? { ...a, status: 'pending' } : a));
      onProtocolChange({ ...saved, approvals });
      setEditing(false);
    } catch {
      alert('Kunde inte spara protokollet.');
    } finally {
      setSaving(false);
    }
  }, [orgId, meetingId, title, content, protocol, onProtocolChange]);

  const submitApproval = useCallback(async (status: string) => {
    if (!orgId) return;
    setSubmittingApproval(true);
    try {
      await approveProtocol(orgId, meetingId, { status, comment: approvalComment || undefined });
      // Re-fetch would be triggered by parent, but we don't have that here.
      // Optimistically update the approval status for the current user.
      if (protocol) {
        const approvals = protocol.approvals.map(a =>
          a.userId === user?.id ? { ...a, status, comment: approvalComment || null } : a
        );
        const allApprovedNow = approvals.every(a => a.status === 'approved');
        onProtocolChange({ ...protocol, approvals, status: allApprovedNow ? 'approved' : protocol.status });
      }
      setApprovalComment('');
      setShowCommentFor(null);
    } catch {
      alert('Kunde inte skicka godkännande.');
    } finally {
      setSubmittingApproval(false);
    }
  }, [orgId, meetingId, approvalComment, protocol, user, onProtocolChange]);

  const approvals = protocol?.approvals ?? [];
  const allApproved = approvals.length > 0 && approvals.every(a => a.status === 'approved');
  const hasRevisionRequest = approvals.some(a => a.status === 'revision_requested');

  // For adding approvers we don't have a separate endpoint; approvers are managed via the
  // protocol approval list. We show available members who aren't yet approvers.
  const existingApproverIds = approvals.map(a => a.userId);
  const availableApprovers = members.filter(m => !existingApproverIds.includes(m.userId));

  if (!protocol) {
    if (!isBoardMember) {
      return (
        <div className="border border-dashed border-gray-300 rounded-xl p-6 text-center">
          <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Inget protokoll tillagt för detta möte</p>
        </div>
      );
    }

    if (editing) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Titel</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                required
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                placeholder="Mötesprotokoll"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Innehåll</label>
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                rows={8}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                placeholder="Protokollets innehåll..."
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 text-gray-600">Avbryt</button>
              <button type="submit" disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? 'Sparar...' : 'Spara protokoll'}
              </button>
            </div>
          </form>
        </div>
      );
    }

    return (
      <div className="border border-dashed border-gray-300 rounded-xl p-6 text-center">
        <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500 mb-3">Inget protokoll tillagt för detta möte</p>
        <button
          onClick={() => { setTitle('Mötesprotokoll'); setContent(''); setEditing(true); }}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" />
          Lägg till protokoll
        </button>
      </div>
    );
  }

  if (editing && isBoardMember) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Titel</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Innehåll</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={8}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
            />
          </div>
          {protocol.approvals.length > 0 && (
            <p className="text-sm text-amber-700 bg-amber-50 px-4 py-2.5 rounded-xl">
              Ändrar du protokollet måste justerarna godkänna det igen. När alla har godkänt skickas det på nytt till medlemmarna.
            </p>
          )}
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? 'Sparar...' : 'Spara'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-4 h-4 text-teal-600" />
              {protocol.title}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {new Date(protocol.createdAt).toLocaleDateString('sv-SE')}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {allApproved && (
              <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Godkänt
              </span>
            )}
            {hasRevisionRequest && !allApproved && (
              <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg">
                <AlertCircle className="w-3.5 h-3.5" />
                Justering begärd
              </span>
            )}
            {!allApproved && !hasRevisionRequest && approvals.length > 0 && (
              <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg">
                <Clock className="w-3.5 h-3.5" />
                Väntar på godkännande
              </span>
            )}
          </div>
        </div>

        {protocol.status === 'approved' && (
          <p className="text-sm text-emerald-700 bg-emerald-50 px-4 py-2.5 rounded-xl mb-4">
            Justerat – skickat till alla medlemmar och sparat i Dokument › Protokoll.
          </p>
        )}

        {protocol.content && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{protocol.content}</p>
          </div>
        )}

        {isBoardMember && (
          <div className="flex gap-2 mb-4">
            <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-teal-600 hover:bg-teal-50 rounded-lg transition-colors">
              Redigera
            </button>
          </div>
        )}

        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
              <UserCheck className="w-4 h-4 text-gray-400" />
              Justerare
            </h4>
            {isBoardMember && approvals.length < 2 && (
              <button
                onClick={() => setShowApproverPicker(!showApproverPicker)}
                className="flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-800 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Lägg till
              </button>
            )}
          </div>

          {showApproverPicker && (
            <div className="mb-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Välj en medlem som justerare:</p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {availableApprovers.length === 0 ? (
                  <p className="text-xs text-gray-400 py-1">Inga fler medlemmar tillgängliga</p>
                ) : (
                  availableApprovers.map(m => (
                    <button
                      key={m.userId}
                      onClick={async () => {
                        try {
                          const newApproval = await addProtocolApprover(orgId, meetingId, m.userId);
                          if (protocol) {
                            onProtocolChange({ ...protocol, approvals: [...protocol.approvals, newApproval] });
                          }
                        } catch {
                          alert('Kunde inte lägga till justerare.');
                        }
                        setShowApproverPicker(false);
                      }}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white hover:shadow-sm text-sm transition-all"
                    >
                      <div className="w-7 h-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {(m.fullName ?? '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{m.fullName}</p>
                        <p className="text-xs text-gray-400">{m.role === 'board_member' ? 'Styrelse' : 'Medlem'}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
              <button onClick={() => setShowApproverPicker(false)} className="mt-2 text-xs text-gray-500 hover:text-gray-700">
                Avbryt
              </button>
            </div>
          )}

          {approvals.length === 0 ? (
            <p className="text-sm text-gray-400">Inga justerare tilldelade</p>
          ) : (
            <div className="space-y-2">
              {approvals.map(a => (
                <div key={a.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      {(a.fullName ?? '?')[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800">{a.fullName}</p>
                      <div className="flex items-center gap-1.5">
                        {a.status === 'approved' && (
                          <span className="flex items-center gap-1 text-xs text-emerald-600">
                            <CheckCircle2 className="w-3 h-3" />
                            Godkänt
                          </span>
                        )}
                        {a.status === 'revision_requested' && (
                          <span className="flex items-center gap-1 text-xs text-amber-600">
                            <AlertCircle className="w-3 h-3" />
                            Justering begärd
                          </span>
                        )}
                        {a.status === 'pending' && (
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <Clock className="w-3 h-3" />
                            Väntar
                          </span>
                        )}
                      </div>
                      {a.comment && (
                        <p className="text-xs text-gray-500 mt-1 flex items-start gap-1">
                          <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          {a.comment}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    {a.userId === user?.id && a.status === 'pending' && (
                      <>
                        {showCommentFor === a.id ? (
                          <div className="flex flex-col gap-1.5">
                            <textarea
                              value={approvalComment}
                              onChange={e => setApprovalComment(e.target.value)}
                              placeholder="Kommentar (valfritt)..."
                              rows={2}
                              className="w-48 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 outline-none resize-none"
                            />
                            <div className="flex gap-1 justify-end">
                              <button
                                onClick={() => submitApproval('approved')}
                                disabled={submittingApproval}
                                className="px-2.5 py-1 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50"
                              >
                                Godkänn
                              </button>
                              <button
                                onClick={() => submitApproval('revision_requested')}
                                disabled={submittingApproval}
                                className="px-2.5 py-1 text-xs font-medium bg-amber-500 text-white rounded-md hover:bg-amber-600 transition-colors disabled:opacity-50"
                              >
                                Begar justering
                              </button>
                              <button
                                onClick={() => { setShowCommentFor(null); setApprovalComment(''); }}
                                className="px-2.5 py-1 text-xs text-gray-500 hover:text-gray-700"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowCommentFor(a.id)}
                            className="px-3 py-1.5 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                          >
                            Granska
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
